import React, {useState, useEffect} from 'react';
import Section from "./Section";
import Table from "./Table";
import { useAuth } from '../helpers/AuthContext';

export default function AdminTasks({}) {
    const { fetch } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        fetch('/admin/tasks.json')
            .then(res => {
                if (!res.ok) throw new Error('Failed to fetch tasks');
                return res.json();
            })
            .then(data => {
                if (isMounted) {
                    setTasks(data);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (isMounted) {
                    setError(err.message);
                    setLoading(false);
                }
            });
        return () => { isMounted = false; };
    }, [fetch]);

    // Column definitions for the Table component
    const columns = [
        {
            key: 'urgency',
            header: 'Urgency',
            width: '100px',
            render: (value) => {
                const colorMap = {
                    'Low': '#28a745',
                    'Medium': '#ffc107',
                    'High': '#fd7e14',
                    'Critical': '#dc3545'
                };
                return (
                    <span style={{
                        backgroundColor: colorMap[value] || '#6c757d',
                        color: value === 'Low' || value === 'Medium' ? '#212529' : '#fff',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        display: 'inline-block'
                    }}>
                        {value}
                    </span>
                );
            }
        },
        {
            key: 'task',
            header: 'Task',
            width: '250px'
        },
        {
            key: 'assignee',
            header: 'Assignee',
            width: '150px',
            render: (value) => {
                if (value === 'Unassigned') {
                    return <span style={{ color: '#dc3545' }}>{value}</span>;
                }
                return value;
            }
        },
        {
            key: 'status',
            header: 'Status',
            width: '120px',
            render: (value) => {
                const statusStyles = {
                    'Not Started': { bg: '#f8f9fa', color: '#212529' },
                    'Pending': { bg: '#e2e3e5', color: '#212529' },
                    'In Progress': { bg: '#cce5ff', color: '#004085' },
                    'Completed': { bg: '#d4edda', color: '#155724' },
                    'Blocked': { bg: '#f8d7da', color: '#721c24' }
                };

                const style = statusStyles[value] || { bg: '#6c757d', color: '#fff' };

                return (
                    <span style={{
                        backgroundColor: style.bg,
                        color: style.color,
                        padding: '3px 8px',
                        borderRadius: '4px',
                        display: 'inline-block'
                    }}>
                        {value}
                    </span>
                );
            }
        },
        {
            key: 'createdAt',
            header: 'Created',
            width: '170px',
            render: (value) => {
                const date = new Date(value);
                return date.toLocaleString();
            }
        },
        {
            key: 'dueDate',
            header: 'Due Date',
            width: '170px',
            render: (value, row) => {
                let dueDate;
                if (value) {
                    dueDate = new Date(value);
                } else {
                    dueDate = new Date(row.createdAt);
                    dueDate.setDate(dueDate.getDate() + 7); // Default to 7 days from creation if no due date
                }
                const now = new Date();
                const isPastDue = dueDate < now;
                const isToday = dueDate.toDateString() === now.toDateString();

                let style = {};
                if (isPastDue) {
                    style = { color: '#dc3545', fontWeight: 'bold' };
                } else if (isToday) {
                    style = { color: '#fd7e14', fontWeight: 'bold' };
                }

                return <span style={style}>{dueDate.toLocaleString()}</span>;
            }
        },
        {
            key: 'actions',
            header: 'Actions',
            width: '120px',
            render: (_, row) => (
                <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                        style={{
                            padding: '2px 8px',
                            fontSize: '0.8rem',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            alert(`Viewing details for task: ${row.task}`);
                        }}
                    >
                        View
                    </button>
                    <button
                        style={{
                            padding: '2px 8px',
                            fontSize: '0.8rem',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            alert(`Marking task as complete: ${row.task}`);
                        }}
                    >
                        Done
                    </button>
                </div>
            )
        }
    ];

    // Filtering and sorting state
    const [filterStatus, setFilterStatus] = useState('All');
    const [filterUrgency, setFilterUrgency] = useState('All');
    const [sortKey, setSortKey] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedRows, setSelectedRows] = useState([]);

    // Status and urgency options
    const statuses = ['All', 'Not Started', 'Pending', 'In Progress', 'Completed', 'Blocked'];
    const urgencyLevels = ['All', 'Low', 'Medium', 'High', 'Critical'];

    // Row click handler
    const handleRowClick = (row) => {
        alert(`Clicked row for task: ${row.task}`);
    };

    // Selection change handler
    const handleSelectionChange = (selected) => {
        setSelectedRows(selected);
    };

    if (loading) return <Section title="Admin Tasks"><div>Loading...</div></Section>;
    if (error) return <Section title="Admin Tasks"><div style={{color: 'red'}}>Error: {error}</div></Section>;

    // Map backend data to table row format
    const rows = tasks.map(task => ({
        id: task.id || task.uuid,
        task: task.title,
        type: task.type,
        urgency: task.urgency,
        assignee: task.Assignee ? (task.Assignee.name || task.Assignee.email) : 'Unassigned',
        reference: task.referenceId || '',
        status: task.status,
        createdAt: task.createdAt,
        dueDate: task.dueDate
    }));

    // Filtering
    const filteredTodos = rows.filter(row => {
        const statusMatch = filterStatus === 'All' || row.status === filterStatus;
        const urgencyMatch = filterUrgency === 'All' || row.urgency === filterUrgency;
        return statusMatch && urgencyMatch;
    });

    // Sorting
    const sortedTodos = [...filteredTodos].sort((a, b) => {
        let aValue = a[sortKey];
        let bValue = b[sortKey];
        if (aValue instanceof Date) aValue = aValue.getTime();
        if (bValue instanceof Date) bValue = bValue.getTime();
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });


    return (
        <Section
            className="admin-tasks"
            title="Admin Tasks"
            preview="Manage your admin tasks here"
            collapsible={true}
        >
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                    <div>
                        <label htmlFor="statusFilter" style={{ marginRight: '5px' }}>Status:</label>
                        <select
                            id="statusFilter"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            style={{ padding: '5px' }}
                        >
                            {statuses.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="urgencyFilter" style={{ marginRight: '5px' }}>Urgency:</label>
                        <select
                            id="urgencyFilter"
                            value={filterUrgency}
                            onChange={(e) => setFilterUrgency(e.target.value)}
                            style={{ padding: '5px' }}
                        >
                            {urgencyLevels.map(urgency => (
                                <option key={urgency} value={urgency}>{urgency}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        style={{
                            marginLeft: 'auto',
                            padding: '5px 10px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                        onClick={() => alert('Create new todo clicked')}
                    >
                        Create New Todo
                    </button>
                </div>
            </div>

            <Table
                data={sortedTodos}
                columns={columns}
                keyField="id"
                sortable={true}
                pagination={true}
                itemsPerPage={5}
                selectable={true}
                onRowClick={handleRowClick}
                onSelectionChange={handleSelectionChange}
                className="admin-todo-table"
                style={{ width: '100%' }}
            />

            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                    style={{
                        padding: '5px 10px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        marginRight: '10px',
                        cursor: 'pointer'
                    }}
                    onClick={() => alert('Bulk delete clicked')}
                >
                    Delete Selected
                </button>
                <button
                    style={{
                        padding: '5px 10px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    onClick={() => alert('Bulk complete clicked')}
                >
                    Complete Selected
                </button>
            </div>
        </Section>
    );
}